/**
 * Accessibility Live Regions
 * Provides screen reader announcements for dynamic content updates
 * WCAG 2.1 Level AA Compliance
 */

(function() {
  'use strict';

  /**
   * Create or get a live region container for announcements
   * @param {string} id - Unique identifier for the region
   * @param {string} politeness - 'polite' or 'assertive' (default: 'polite')
   * @returns {HTMLElement} The live region element
   */
  function ensureLiveRegion(id = 'a11y-announcements', politeness = 'polite') {
    let region = document.getElementById(id);
    
    if (!region) {
      region = document.createElement('div');
      region.id = id;
      region.setAttribute('aria-live', politeness);
      region.setAttribute('aria-atomic', 'true');
      region.className = 'sr-only';
      
      // Visually hidden but still in the DOM for screen readers
      region.style.position = 'absolute';
      region.style.left = '-10000px';
      region.style.width = '1px';
      region.style.height = '1px';
      region.style.overflow = 'hidden';
      region.style.clip = 'rect(0, 0, 0, 0)';
      region.style.whiteSpace = 'nowrap';
      region.style.borderWidth = '0';
      
      document.body.appendChild(region);
    }
    
    return region;
  }

  /**
   * Announce a message to screen readers
   * @param {string} message - The message to announce
   * @param {string} regionId - The live region ID to use
   * @param {string} politeness - 'polite' or 'assertive'
   */
  function announce(message, regionId = 'a11y-announcements', politeness = 'polite') {
    const region = ensureLiveRegion(regionId, politeness);
    
    // Clear previous content
    region.textContent = '';
    
    // Use setTimeout to ensure the screen reader detects the change
    setTimeout(() => {
      region.textContent = message;
    }, 100);
    
    // Clear after announcement to allow repeated messages
    setTimeout(() => {
      region.textContent = '';
    }, 3000);
  }

  /**
   * Announce form validation errors
   * @param {string} fieldName - Name of the field with the error
   * @param {string} error - The error message
   */
  function announceError(fieldName, error) {
    announce(
      `Error in ${fieldName}: ${error}`,
      'form-error-announcements',
      'assertive'
    );
  }

  /**
   * Announce successful form submission
   * @param {string} message - Success message
   */
  function announceSuccess(message) {
    announce(
      message,
      'form-success-announcements',
      'polite'
    );
  }

  /**
   * Announce product variant change
   * @param {string} variantName - Name of the variant
   */
  function announceVariantChange(variantName) {
    announce(
      `Product variant changed to ${variantName}`,
      'product-variant-announcements',
      'polite'
    );
  }

  /**
   * Announce cart updates
   * @param {string} productName - Name of the product
   * @param {number} quantity - Quantity added
   * @param {string} action - 'added' or 'removed'
   */
  function announceCartUpdate(productName, quantity = 1, action = 'added') {
    const message = quantity === 1
      ? `${productName} ${action} to cart`
      : `${quantity} of ${productName} ${action} to cart`;
    
    announce(
      message,
      'cart-announcements',
      'polite'
    );
  }

  /**
   * Handle custom events for accessibility
   */
  
  // Listen for cart updates
  document.addEventListener('cart:add', function(e) {
    if (e.detail) {
      announceCartUpdate(
        e.detail.product_name || 'Item',
        e.detail.quantity || 1,
        'added'
      );
    }
  });

  document.addEventListener('cart:remove', function(e) {
    if (e.detail) {
      announceCartUpdate(
        e.detail.product_name || 'Item',
        e.detail.quantity || 1,
        'removed'
      );
    }
  });

  // Listen for variant changes
  document.addEventListener('variant:changed', function(e) {
    if (e.detail) {
      announceVariantChange(e.detail.variant_name || 'Variant');
    }
  });

  // Listen for form validation
  document.addEventListener('form:validation-error', function(e) {
    if (e.detail) {
      announceError(e.detail.field_name, e.detail.error_message);
    }
  });

  document.addEventListener('form:submitted', function(e) {
    if (e.detail) {
      announceSuccess(e.detail.message || 'Form submitted successfully');
    }
  });

  // Export globally for use in other scripts
  window.a11y = {
    announce: announce,
    announceError: announceError,
    announceSuccess: announceSuccess,
    announceVariantChange: announceVariantChange,
    announceCartUpdate: announceCartUpdate,
    ensureLiveRegion: ensureLiveRegion
  };

  // Log that accessibility module is loaded
  console.log('Accessibility module loaded - window.a11y is available');

})();
